<template>
    <div :class="[calendarClass, 'vdp-datepicker__calendar']" v-show="showDayView" :style="calendarStyle">
        <slot name="beforeCalendarHeader"></slot>
        <!-- *********************************************
        *                      Header                    *
        ********************************************** -->
        <header class="date-picker-header">
            <!-- Year Section -->
            <div class="year-section">
                <!-- Arrow Left -->
                <buttons-quaternary
                icon="arrow-left"
                buttonStyle="round"
                :disabled="isYearLeftNavDisabled"
                @clicked="methods.previousYear"/>
                <!-- Year Name -->
				<div class="name" :class="allowedToShowView('month') ? 'up' : ''">
                    <span style="padding-left: 40px">{{currYearName}}</span>
                    <buttons-quaternary
                    buttonStyle="round"
                    icon="arrow-down-sort"
                    @clicked="data.showYearList = true"/>
                </div>
                <div v-if="data.showYearList" class="dropdown-wrapper">
                    <div class="options-body dont-close-side-modals">
                        <div
                        v-for="year in yearsList"
                        :key="year"
                        class="row"
                        :class="{
                            'selected': year.id === utils.getFullYear(props.pageDate),
                            'disabled': methods.checkIfYearIsDisabled(year.id)
                        }"
                        @click="methods.changeToSpecificYear(year.id)">
                            {{ year.id }}
                            <check-line v-if="year.id === utils.getFullYear(props.pageDate)"/>
                        </div>
                    </div>
                    <div class="footer-area">
                        <button-secondary
                        label="Cancelar"
                        buttonSize="tiny"
                        @click="methods.closeYearList"/>
                    </div>
                </div>
                <!-- Arrow Right -->
                <buttons-quaternary
                icon="arrow-right"
                buttonStyle="round"
                :disabled="isYearRightNavDisabled"
                @clicked="methods.nextYear"/>
			</div>
            <!-- Month Section -->
			<div class="month-section">
                <!-- Arrow Left -->
                <buttons-quaternary
                icon="arrow-left"
                buttonStyle="round"
                :disabled="isMonthLeftNavDisabled"
                @clicked="methods.previousMonth"/>
                <!-- Month Name -->
				<div class="name" :class="allowedToShowView('month') ? 'up' : ''">
                    <span style="padding-left: 40px">{{currMonthName}}</span>
                    <buttons-quaternary
                    buttonStyle="round"
                    icon="arrow-down-sort"
                    @clicked="data.showMonthList = true"/>
                </div>
                <div v-if="data.showMonthList" class="dropdown-wrapper">
                    <div class="options-body dont-close-side-modals">
                        <div
                        v-for="month in monthList"
                        :key="month"
                        class="row"
                        :class="{
                            'selected': month.id === utils.getMonth(props.pageDate),
                            'disabled': methods.checkIfMonthIsDisabled(month.id)
                        }"
                        @click="methods.changeToSpecificMonth(month.id)">
                            {{ month.name }}
                            <check-line v-if="month.id === utils.getMonth(props.pageDate)"/>
                        </div>
                    </div>
                    <div class="footer-area">
                        <button-secondary
                        label="Cancelar"
                        buttonSize="tiny"
                        @click="methods.closeMonthList"/>
                    </div>
                </div>
                <!-- Arrow Right -->
                <buttons-quaternary
                icon="arrow-right"
                buttonStyle="round"
                :disabled="isMonthRightNavDisabled"
                @clicked="methods.nextMonth"/>
			</div>
        </header>
        <!-- *********************************************
        *                       Body                     *
        ********************************************** -->
        <div
        class="calendar-body"
        :class="{ 'background-on-weekend': setBackgroundOnWeekend, 'monday-first': mondayFirst }">
            <div :class="[isRtl ? 'flex-rtl' : 'flex-style', {'no-week-days': !displayWeekDays}]">
                <!-- Week Days Names display -->
                <div v-if="displayWeekDays" class="day-headers">
                    <div
                    class="day-header"
                    v-for="(d, index) in daysOfWeek"
                    :class="{'weekend': mondayFirst && (index == 5 || index == 6) || !mondayFirst && (index == 0 || index == 6)}"
                    :key="d.timestamp">
                        <span>{{ d }}</span>
                    </div>
                </div>

                <!-- blank state in days -->
                <!-- <template v-if="blankDays > 0">
                    <div class="cell day blank" v-for="d in blankDays" :key="d.timestamp"/>
                </template> -->

                <!-- prev month days -->
                <div
                class="cell day other-month"
                v-for="day in prevMonthDays"
                :class="{weekend: day.isWeekend, 'disabled': disableWeekends && day.isWeekend}"
                :key="day.key"
				v-html="`<span></span>`"
				@click.stop=""/>
                <!-- month days -->
                <div
                class="cell day"
                v-for="day in days"
                :key="day.timestamp"
                :class="methods.dayClasses(day)"
                v-html="`<span>${dayCellContent(day)}</span>`"
                @click.stop="methods.selectDate(day)"/>
                <!-- next month days -->
                <div
                class="cell day other-month"
                v-for="day in nextMonthDays"
                :class="{weekend: day.isWeekend, 'disabled': disableWeekends && day.isWeekend}"
                :key="day.key"
				v-html="`<span></span>`"
				@click.stop=""/>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue'
import moment from '@/assets/setups/moment'

import CheckLine from '@/assets/icons/check-line.svg?inline'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import ButtonsQuaternary from '@/components/generic/buttons/Quaternary'
import { makeDateUtils } from '@/assets/localPlugins/datepicker/utils/DateUtils'

/**
 * *************** PROPS ***************
 */

const props = defineProps({
    showDayView: Boolean,
    selectedDate: Date,
    pageDate: Date,
    pageTimestamp: Number,
    fullMonthName: Boolean,
    allowedToShowView: Function,
    dayCellContent: {
        type: Function,
        default: day => day.date
    },
    disabledDates: Object,
    highlighted: Object,
    calendarClass: [String, Object, Array],
    calendarStyle: Object,
    translation: Object,
    isRtl: Boolean,
    mondayFirst: Boolean,
    useUtc: Boolean,
    /* added props */
    showCancelButton: Boolean,
    cancelButtonText: String,
    rightArrowIcon: String,
    leftArrowIcon: String,
    showOtherMonthDays: Boolean,
    setBackgroundOnWeekend: Boolean,
    monthOnTheRight: Boolean,
    numberOfYearsToDisplay: {
        type: Number,
        default: 100
    },
    displayWeekDays: {
        type: Boolean,
        default: true
    },
    disableWeekends: {
        type: Boolean,
        default: false
    }
})

/**
 * ************* EMITS ***************
 */

const emit = defineEmits([
    'selectedDisabled',
    'selectDate',
    'clearDate',
    'closeCalendar',
    'changedMonth',
    'showMonthCalendar'
])

/**
 * *************** DATA ***************
 */

const data = ref({
    showMonthList: false,
    showYearList: false,
    pressingElem: false
})

// refs

const yearsGroup = ref({})

/**
 * *************** COMPUTED ***************
 */

const utils = computed(() => makeDateUtils(props.useUtc))

// FIXME: only place using moment.js for now, importing external library
// only for this, maybe can be avoided or use moment in more places
const monthList = computed(() => {
    const months = moment.months()
    var monthsArray = []
    months.forEach((month, index) => {
        monthsArray.push({
            name: month,
            id: index
        })
    })
    return monthsArray
})
const yearsList = computed(() => {
    const newYear = new Date().getFullYear()
    var yearArray = []

    /* If we have to and from disabled dates we need to only show the years between these two dates */
    if (props.disabledDates && props.disabledDates.to && props.disabledDates.from) {
        const toYear = new Date(props.disabledDates.to).getFullYear()
        const fromYear = new Date(props.disabledDates.from).getFullYear()

        for (let i = 0; i <= Math.abs(toYear - fromYear); i++) {
            yearArray.unshift({ id: toYear + i })
        }

        return yearArray
    }

    /* If the picker is from today to future we need to show years from today to future */
    if (props.disabledDates && props.disabledDates.to && new Date(props.disabledDates.to).getFullYear() <= newYear) {
        for (let i = parseInt(newYear) + 20; i >= newYear; i--) {
            yearArray.unshift({
                id: i
            })
        }
        return yearArray
    } else {
        for (let i = parseInt(newYear) - props.numberOfYearsToDisplay; i <= newYear + 1; i++) {
            yearArray.unshift({
                id: i
            })
        }
    }
    return yearArray
})
/**
* Returns an array of day names
* @return {String[]}
*/
const daysOfWeek = computed(() => {
    if (props.mondayFirst) {
        const tempDays = props.translation.days.slice()
        tempDays.push(tempDays.shift())
        return tempDays
    }
    return props.translation.days
})
/**
* Returns the day number of the week less one for the first of the current month
* Used to show amount of empty cells before the first in the day calendar layout
* @return {Number}
*/
const blankDays = computed(() => {
    const d = props.pageDate
    const dObj = props.useUtc
        ? new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
        : new Date(d.getFullYear(), d.getMonth(), 1, d.getHours(), d.getMinutes())
    if (props.mondayFirst) {
        return utils.value.getDay(dObj) > 0 ? utils.value.getDay(dObj) - 1 : 6
    }
    return utils.value.getDay(dObj)
})
/**
* Returns the days of the previous month that sill appear in the calendar
* @return {Array}
*/
const prevMonthDays = computed(() => {
    if (!props.showOtherMonthDays) return []
    const d = props.pageDate
    const currDateObj = new Date(d.getFullYear(), d.getMonth(), 1, d.getHours(), d.getMinutes())
    // day of week for 1st day of month
    // aka number of blank days to insert before
    const numDays = !props.mondayFirst ? currDateObj.getDay() : currDateObj.getDay() - 1
    // get last day of previous month
    const prevDateObj = new Date(d.getFullYear(), d.getMonth(), 0, d.getHours(), d.getMinutes())
    const lastDay = prevDateObj.getDate()
    // first day of previous month to show
    const start = lastDay - (numDays + 1)
    /* add remaining days of previous month */
    const arr = []
	const endFor = numDays < 0 ? 6 : numDays
    for (var i = 0; i < endFor; i++) {
        arr.push({
            value: start + i,
            key: `${start + i}${Date.now()}`
        })
    }
    return arr
})
/**
* Returns the days of the next month that sill appear in the calendar
* @return {Array}
*/
const nextMonthDays = computed(() => {
    if (!props.showOtherMonthDays) return []
    const d = props.pageDate
    const dObj = new Date(d.getFullYear(), (d.getMonth() + 1), 0, d.getHours(), d.getMinutes())
    // day of week for last day of the current month
    const lastDay = dObj.getDay()
    // number of blank days to insert after
    const mondayFirstControl = props.mondayFirst ? 0 : 1
    const numDays = 7 - (lastDay + mondayFirstControl)
    // return array of days to show after
    const arr = []
    for (var i = 1; i <= numDays; i++) {
        arr.push({
            value: i,
            key: `${i}${Date.now()}`
        })
    }
    return arr
})
/**
* @return {Object[]}
*/
const days = computed(() => {
    const d = props.pageDate
    const days = []
    // set up a new date object to the beginning of the current 'page'
    const dObj = props.useUtc
        ? new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
        : new Date(d.getFullYear(), d.getMonth(), 1, d.getHours(), d.getMinutes())
    const daysInMonth = utils.value.daysInMonth(utils.value.getFullYear(dObj), utils.value.getMonth(dObj))
    for (let i = 0; i < daysInMonth; i++) {
        days.push({
            date: utils.value.getDate(dObj),
            timestamp: dObj.getTime(),
            isSelected: methods.isSelectedDate(dObj),
            isDisabled: methods.isDisabledDate(dObj),
            isHighlighted: methods.isHighlightedDate(dObj),
            isHighlightStart: methods.isHighlightStart(dObj),
            isHighlightEnd: methods.isHighlightEnd(dObj),
            isToday: utils.value.compareDates(dObj, new Date()),
            isWeekend: utils.value.getDay(dObj) === 0 || utils.value.getDay(dObj) === 6,
            isSaturday: utils.value.getDay(dObj) === 6,
            isSunday: utils.value.getDay(dObj) === 0
        })
        utils.value.setDate(dObj, utils.value.getDate(dObj) + 1)
    }
    return days
})
/**
* Gets the name of the month the current page is on
* @return {String}
*/
const currMonthName = computed(() => {
    const monthName = props.fullMonthName ? props.translation.months : props.translation.monthsAbbr
    return utils.value.getMonthNameAbbr(utils.value.getMonth(props.pageDate), monthName)
})
/**
* Gets the name of the year that current page is on
* @return {Number}
*/
const currYearName = computed(() => {
    const yearSuffix = props.translation.yearSuffix
    return `${utils.value.getFullYear(props.pageDate)}${yearSuffix}`
})
/**
* Is this translation using year/month/day format?
* @return {Boolean}
*/
const isYmd = computed(() => {
    return props.translation.ymd && props.translation.ymd === true
})
/**
* Is the left hand navigation button disabled?
* @return {Boolean}
*/
const isMonthLeftNavDisabled = computed(() => {
    return props.isRtl
        ? methods.isNextMonthDisabled(props.pageTimestamp)
        : methods.isPreviousMonthDisabled(props.pageTimestamp)
})
/**
* Is the right hand navigation button disabled?
* @return {Boolean}
*/
const isMonthRightNavDisabled = computed(() => {
    return props.isRtl
        ? methods.isPreviousMonthDisabled(props.pageTimestamp)
        : methods.isNextMonthDisabled(props.pageTimestamp)
})

/**
* Is the left hand navigation button disabled?
* @return {Boolean}
*/
const isYearLeftNavDisabled = computed(() => {
    return props.isRtl
        ? methods.isNextYearDisabled(props.pageTimestamp)
        : methods.isPreviousYearDisabled(props.pageTimestamp)
})
/**
* Is the right hand navigation button disabled?
* @return {Boolean}
*/
const isYearRightNavDisabled = computed(() => {
    return props.isRtl
        ? methods.isPreviousYearDisabled(props.pageTimestamp)
        : methods.isNextYearDisabled(props.pageTimestamp)
})

/**
 * *************** METHODS ***************
 */
let timeout

const methods = {
    selectDate (date) {
        if (date.isDisabled) {
            emit('selectedDisabled', date)
            return false
        }
        emit('selectDate', date)
    },

    /* Added Year Section */
    clearCalendar () {
        emit('clearDate')
        emit('closeCalendar')
    },
    changeYear (incrementBy) {
        const date = props.pageDate
        utils.value.setFullYear(date, utils.value.getFullYear(date) + incrementBy)
        emit('changedMonth', date)
    },
    /**
    * Is the previous year disabled?
    * @return {Boolean}
    */
    isPreviousYearDisabled () {
        if (!props.disabledDates || !props.disabledDates.to) {
            return false
        }
        const d = props.pageDate
        return utils.value.getFullYear(props.disabledDates.to) >= utils.value.getFullYear(d)
    },
    previousYear () {
        if (!methods.isPreviousYearDisabled()) {
            methods.changeYear(-1)
        }
    },
    /**
    * Is the next year disabled?
    * @return {Boolean}
    */
    isNextYearDisabled () {
        if (!props.disabledDates || !props.disabledDates.from) {
            return false
        }
        const d = props.pageDate
        return utils.value.getFullYear(props.disabledDates.from) <= utils.value.getFullYear(d)
    },
    nextYear () {
        if (!methods.isNextYearDisabled()) {
            methods.changeYear(+1)
        }
    },
    changeToSpecificYear ($event) {
        const date = props.pageDate
        utils.value.setFullYear(date, $event)
        emit('changedMonth', date)
        data.value.showYearList = false
    },
    checkIfYearIsDisabled (year) {
        if (props.disabledDates.from && props.disabledDates.to) {
            return !((year <= utils.value.getFullYear(props.disabledDates.from)) && (year >= utils.value.getFullYear(props.disabledDates.to)))
        } else if (props.disabledDates.from) {
            return year > utils.value.getFullYear(props.disabledDates.from)
        } else if (props.disabledDates.to) {
            return year < utils.value.getFullYear(props.disabledDates.to)
        }

        return false
    },
    /* end added Year Section */

    /**
    * @return {Number}
    */
    getPageMonth () {
        return utils.value.getMonth(props.pageDate)
    },
    /**
    * Emit an event to show the month picker
    */
    showMonthCalendar () {
        emit('showMonthCalendar')
    },
    /**
    * Change the page month
    * @param {Number} incrementBy
    */
    changeMonth (incrementBy) {
        const date = props.pageDate
        utils.value.setMonth(date, utils.value.getMonth(date) + incrementBy)
        emit('changedMonth', date)
    },
    /**
    * Decrement the page month
    */
    previousMonth () {
        if (!methods.isPreviousMonthDisabled()) {
            methods.changeMonth(-1)
        }
    },
    changeToSpecificMonth ($event) {
        const date = props.pageDate
        utils.value.setMonth(date, $event)
        emit('changedMonth', date)
        data.value.showMonthList = false
    },
    /**
    * Is the previous month disabled?
    * @return {Boolean}
    */
    isPreviousMonthDisabled () {
        if (!props.disabledDates || !props.disabledDates.to) {
            return false
        }
        const d = props.pageDate
        return utils.value.getMonth(props.disabledDates.to) >= utils.value.getMonth(d) &&
        utils.value.getFullYear(props.disabledDates.to) >= utils.value.getFullYear(d)
    },
    /**
    * Increment the current page month
    */
    nextMonth () {
        if (!methods.isNextMonthDisabled()) {
            methods.changeMonth(+1)
        }
    },
    /**
    * Increment the current page month
    */
    goToClickedMonth ($event) {
        if (!methods.isNextMonthDisabled()) {
            methods.changeMonth(+1)
        }
    },
    /**
    * Is the next month disabled?
    * @return {Boolean}
    */
    isNextMonthDisabled () {
        if (!props.disabledDates || !props.disabledDates.from) {
            return false
        }
        const d = props.pageDate
        return utils.value.getMonth(props.disabledDates.from) <= utils.value.getMonth(d) &&
        utils.value.getFullYear(props.disabledDates.from) <= utils.value.getFullYear(d)
    },
    /**
    * Is the month disabled?
    * @return {Boolean}
    */
    checkIfMonthIsDisabled (month) {
        if (props.disabledDates.from && props.disabledDates.to && (utils.value.getFullYear(props.pageDate) === utils.value.getFullYear(props.disabledDates.to)) && (utils.value.getFullYear(props.pageDate) === utils.value.getFullYear(props.disabledDates.from))) {
            return !((month <= utils.value.getMonth(props.disabledDates.from)) && (month >= utils.value.getMonth(props.disabledDates.to)))
        } else if (props.disabledDates.from && (utils.value.getFullYear(props.pageDate) === utils.value.getFullYear(props.disabledDates.from))) {
            return month > utils.value.getMonth(props.disabledDates.from)
        } else if (props.disabledDates.to && (utils.value.getFullYear(props.pageDate) === utils.value.getFullYear(props.disabledDates.to))) {
            return month < utils.value.getMonth(props.disabledDates.to)
        }

        return false
    },
    /**
    * Whether a day is selected
    * @param {Date}
    * @return {Boolean}
    */
    isSelectedDate (dObj) {
        return props.selectedDate && utils.value.compareDates(props.selectedDate, dObj)
    },
    /**
    * Whether a day is disabled
    * @param {Date}
    * @return {Boolean}
    */
    isDisabledDate (date) {
        let disabledDates = false
        const disabledDateTo = props.disabledDates?.to ? methods.resetDateHours(props.disabledDates.to) : null
        const disabledDateFrom = props.disabledDates?.from ? methods.resetDateHours(props.disabledDates.from) : null

        if (typeof props.disabledDates === 'undefined') {
            return false
        }

        if (typeof props.disabledDates.dates !== 'undefined') {
            props.disabledDates.dates.forEach((d) => {
                if (utils.value.compareDates(date, d)) {
                    disabledDates = true
                    return true
                }
            })
        }
        if (typeof props.disabledDates.to !== 'undefined' && props.disabledDates.to && methods.resetDateHours(date) < disabledDateTo) {
            disabledDates = true
        }
        if (typeof props.disabledDates.from !== 'undefined' && props.disabledDates.from && methods.resetDateHours(date) > disabledDateFrom) {
            disabledDates = true
        }
        if (typeof props.disabledDates.ranges !== 'undefined') {
            props.disabledDates.ranges.forEach((range) => {
                if (typeof range.from !== 'undefined' && range.from && typeof range.to !== 'undefined' && range.to) {
                    if (date < range.to && date > range.from) {
                        disabledDates = true
                        return true
                    }
                }
            })
        }
        if (typeof props.disabledDates.days !== 'undefined' && props.disabledDates.days.indexOf(utils.value.getDay(date)) !== -1) {
            disabledDates = true
        }
        if (typeof props.disabledDates.daysOfMonth !== 'undefined' && props.disabledDates.daysOfMonth.indexOf(utils.value.getDate(date)) !== -1) {
            disabledDates = true
        }
        if (typeof props.disabledDates.customPredictor === 'function' && props.disabledDates.customPredictor(date)) {
            disabledDates = true
        }
        return disabledDates
    },
    /**
    * parse a date to rest time to zero, so when comparing only day/month/year matter
    * @param {Date}
    * @return {Date}
    */
    resetDateHours (date) {
        // added this because we receive the date in a moment format
        var newTempDate = new Date(date)
        return newTempDate.setHours(0, 0, 0, 0)
    },
    /**
    * Whether a day is highlighted (only if it is not disabled already except when highlighted.includeDisabled is true)
    * @param {Date}
    * @return {Boolean}
    */
    isHighlightedDate (date) {
        if (!(props.highlighted && props.highlighted.includeDisabled) && methods.isDisabledDate(date)) {
            return false
        }

        let highlighted = false

        if (typeof props.highlighted === 'undefined') {
            return false
        }

        if (typeof props.highlighted.dates !== 'undefined') {
            props.highlighted.dates.forEach((d) => {
                if (utils.value.compareDates(date, d)) {
                    highlighted = true
                    return true
                }
            })
        }

        if (methods.isDefined(props.highlighted.from) && methods.isDefined(props.highlighted.to)) {
            highlighted = date >= props.highlighted.from && date <= props.highlighted.to
        }

        if (typeof props.highlighted.days !== 'undefined' && props.highlighted.days.indexOf(utils.value.getDay(date)) !== -1) {
            highlighted = true
        }

        if (typeof props.highlighted.daysOfMonth !== 'undefined' && props.highlighted.daysOfMonth.indexOf(utils.value.getDate(date)) !== -1) {
            highlighted = true
        }

        if (typeof props.highlighted.customPredictor === 'function' && props.highlighted.customPredictor(date)) {
            highlighted = true
        }

        return highlighted
    },
    dayClasses (day) {
        return {
            selected: day.isSelected,
            disabled: day.isDisabled || (day.isWeekend && props.disableWeekends),
            highlighted: day.isHighlighted,
            today: day.isToday,
            weekend: day.isWeekend,
            sat: day.isSaturday,
            sun: day.isSunday,
            'highlight-start': day.isHighlightStart,
            'highlight-end': day.isHighlightEnd
        }
    },
    /**
    * Whether a day is highlighted and it is the first date
    * in the highlighted range of dates
    * @param {Date}
    * @return {Boolean}
    */
    isHighlightStart (date) {
        return methods.isHighlightedDate(date) &&
        (props.highlighted.from instanceof Date) &&
        (utils.value.getFullYear(props.highlighted.from) === utils.value.getFullYear(date)) &&
        (utils.value.getMonth(props.highlighted.from) === utils.value.getMonth(date)) &&
        (utils.value.getDate(props.highlighted.from) === utils.value.getDate(date))
    },
    /**
    * Whether a day is highlighted and it is the first date
    * in the highlighted range of dates
    * @param {Date}
    * @return {Boolean}
    */
    isHighlightEnd (date) {
        return methods.isHighlightedDate(date) &&
        (props.highlighted.to instanceof Date) &&
        (utils.value.getFullYear(props.highlighted.to) === utils.value.getFullYear(date)) &&
        (utils.value.getMonth(props.highlighted.to) === utils.value.getMonth(date)) &&
        (utils.value.getDate(props.highlighted.to) === utils.value.getDate(date))
    },
    /**
    * Helper
    * @param  {mixed}  prop
    * @return {Boolean}
    */
    isDefined (prop) {
        return typeof prop !== 'undefined' && prop
    },
    /* Method to close Month List */
    closeMonthList () {
        data.value.showMonthList = false
    },
    /* Method to close Year List */
    closeYearList () {
        data.value.showYearList = false
    },
    mousedownHandlerUp ($event) {
        timeout = setInterval(() => {
            var currentPosition = yearsGroup.value.scrollTop
            yearsGroup.value.scrollTop = currentPosition - 3
        }, 0) // Sets the speed

        // return false
    },
    clearScrollInterval ($event) {
        clearInterval(timeout)
        return false
    },
    mousedownHandlerDown ($event) {
        timeout = setInterval(() => {
            const currentPosition = yearsGroup.value.scrollTop
            yearsGroup.value.scrollTop = currentPosition + 3
        }, 0) // Sets the speed

        // return false
    }
}

/**
 * ***************   HOOKS   *************
 */

onMounted(() => {
    /* check if we need to swap month and year position */
    const month = document.getElementById('month-section')
    const year = document.getElementById('year-section')
    const separator = document.getElementById('picker-separator')
    if (separator) {
        props.monthOnTheRight ? separator.after(month) : separator.after(year)
        props.monthOnTheRight ? separator.before(year) : separator.before(month)
    }
})
</script>
<style lang="scss" scoped>
.name {
    display: flex;
    align-items: center;
    justify-content: center;
}

.dropdown-wrapper {
    width: 100%;
	height: 100%;

	display: flex;
	flex-direction: column;

	padding-top: 8px;

	position: absolute;
	top: 0;
	left: 0;
	z-index: 5;

	background-color: var(--secondary-light-4);
	box-shadow: var(--box-shadow-2);
	overflow-x: hidden;
	border-radius: 8px;

    .options-body {
        width: 100%;
        height: auto;

        @include generic-scrollbar(var(--secondary-light-4), var(--primary));

        .row {
            height: 40px;
            padding: 12px 16px;

            display: flex;
            align-items: center;
            justify-content: space-between;

            @include H7-Medium;
            line-height: 16px;
            color: var(--secondary-dark-2);
            text-transform: capitalize;

            cursor: pointer;

            &.selected {
                color: var(--primary);

                :deep(svg) {
                    .change-color-fill {
                        fill: var(--primary);
                    }
                }
            }

            &.disabled {
                opacity: 0.2;
                pointer-events: none;
                user-select: none;
                cursor: default;

                @media (hover: hover) and (pointer: fine) {
                    &:hover {
                        background-color: var(--secondary-light-2);
                    }
                }
            }

            @media (hover: hover) and (pointer: fine) {
                &:hover {
                    background-color: var(--button-secondary-state-hover);
                }
            }

            &:active {
                background-color: var(--button-secondary-state-press);
            }

            .button-quaternary {
                position: fixed;
            }
        }
    }
    .footer-area {
        height: 64px;
        padding: 16px;

        border-top: 1px solid var(--secondary-light-2);
    }
}
</style>
